import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  Bold,
  Italic,
  Strikethrough,
  Underline as UnderlineIcon,
  Minus,
  Quote,
  List,
  ListOrdered,
  Link2,
  Link2Off,
} from 'lucide-react';

interface Props {
  value: string; // Markdown
  onChange: (markdown: string) => void;
  disabled?: boolean;
  /** Hides the formatting toolbar for this instance only (29.09.2026 — Stornobedingungen
   *  soll vorerst nicht formatierbar sein). The toolbar stays a capability of the
   *  component, just not rendered here — other call sites are unaffected. */
  hideToolbar?: boolean;
}

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6] as const;

/**
 * Schemes a link may use.
 *
 * Deliberately short. The description is rendered in the storefront, so a
 * `javascript:` or `data:` URL saved here would execute in a visitor's browser —
 * the classic stored-XSS route. tiptap's own validation is kept on top of this
 * (defaultValidate below) rather than replaced.
 */
export const ALLOWED_PROTOCOLS = ['http', 'https', 'mailto', 'tel'];

/**
 * Adds https:// to something that looks like a bare domain.
 *
 * Without it "jacques.de" would be stored as a relative path and resolve against the
 * storefront instead of the intended site — a broken link that looks correct in the
 * editor.
 */
/**
 * True when the URL carries no scheme (relative) or an allowed one.
 *
 * Separate from tiptap's own validation so it can be tested directly: this is the check
 * that keeps `javascript:` out of the storefront, and it should not need a mounted
 * editor to verify.
 */
export function hasAllowedScheme(url: string): boolean {
  const match = /^([a-zA-Z][a-zA-Z0-9+.-]*):/.exec(url.trim());
  if (!match) return true; // relative or bare domain — no scheme to reject

  return ALLOWED_PROTOCOLS.includes(match[1]!.toLowerCase());
}

export function normalizeUrl(raw: string): string {
  const url = raw.trim();
  if (!url) return '';
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url) || url.startsWith('/')) return url;
  return `https://${url}`;
}

export function RichTextEditor({ value, onChange, disabled = false, hideToolbar = false }: Props) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        protocols: ALLOWED_PROTOCOLS,
        // Links open in a new tab; noopener/noreferrer because the target is
        // user-supplied and must not get a handle on the opener window.
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer nofollow' },
        // Clicking inside the editor should place the cursor, not navigate away and
        // lose the unsaved form.
        openOnClick: false,
        isAllowedUri: (url, { defaultValidate }) =>
          hasAllowedScheme(url) && defaultValidate(url),
      }),
      Markdown.configure({
        // Raw HTML inside markdown is escaped to text rather than parsed/rendered —
        // the same threat sanitizeEditorMarkdown guards against, kept off at the parser
        // itself rather than relying solely on the backend sanitizer.
        html: false,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => onChange(editor.storage.markdown.getMarkdown()),
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[160px] rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ring',
      },
    },
  });

  // Keep editor content in sync when the form resets (e.g. switching events), and also
  // whenever `editor` itself transitions from null to a real instance without `value`
  // changing in the same render (29.09.2026 — closes a gap where the very first sync
  // check could run before the editor existed and never get a second chance once it did).
  useEffect(() => {
    if (editor && value !== editor.storage.markdown.getMarkdown()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  const currentHeading = HEADING_LEVELS.find((l) => editor.isActive('heading', { level: l }));
  const headingValue = currentHeading ? `h${currentHeading}` : 'p';

  /**
   * Opens the link dialog, prefilled when the cursor sits on an existing link.
   *
   * Prefilling is what makes editing possible at all: without it the only way to change
   * a link would be to remove and retype it, and the href is not visible in the editor.
   */
  const openLinkDialog = () => {
    setLinkUrl((editor.getAttributes('link').href as string | undefined) ?? '');
    setLinkOpen(true);
  };

  const applyLink = () => {
    const url = normalizeUrl(linkUrl);

    if (!url) {
      // An emptied field means "remove the link" — the same as the unlink button, but
      // reachable without knowing that button exists.
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      return;
    }

    // extendMarkRange so editing works with the cursor anywhere inside the link, not
    // only when the whole text is selected.
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setLinkOpen(false);
  };

  const ToolbarButton = ({
    active,
    onClick,
    label,
    children,
  }: {
    active?: boolean;
    onClick: () => void;
    label: string;
    children: React.ReactNode;
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn('h-8 w-8', active && 'bg-muted text-foreground')}
    >
      {children}
    </Button>
  );

  return (
    <div className="space-y-2">
      {!disabled && !hideToolbar && (
        <div className="flex flex-wrap items-center gap-1 rounded-md border bg-muted/30 p-1">
          <Select
            value={headingValue}
            onValueChange={(v) => {
              if (v === 'p') editor.chain().focus().setParagraph().run();
              else
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: Number(v.slice(1)) as 1 | 2 | 3 | 4 | 5 | 6 })
                  .run();
            }}
          >
            <SelectTrigger className="h-8 w-[130px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="p">Absatz</SelectItem>
              {HEADING_LEVELS.map((l) => (
                <SelectItem key={l} value={`h${l}`}>
                  Überschrift {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <ToolbarButton active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} label="Fett">
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} label="Kursiv">
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} label="Unterstrichen">
            <UnderlineIcon className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} label="Durchgestrichen">
            <Strikethrough className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Trennlinie">
            <Minus className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} label="Zitat">
            <Quote className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Aufzählung">
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Nummerierte Liste">
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive('link')}
            onClick={openLinkDialog}
            label={editor.isActive('link') ? 'Link bearbeiten' : 'Link einfügen'}
          >
            <Link2 className="h-4 w-4" />
          </ToolbarButton>
          {editor.isActive('link') && (
            <ToolbarButton
              onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
              label="Link entfernen"
            >
              <Link2Off className="h-4 w-4" />
            </ToolbarButton>
          )}
        </div>
      )}
      <EditorContent editor={editor} />

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {editor.isActive('link') ? 'Link bearbeiten' : 'Link einfügen'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-1">
            <Input
              autoFocus
              placeholder="https://www.jacques.de"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Erlaubt sind Web-Adressen sowie mailto: und tel:. Ohne Angabe von https://
              wird es ergänzt. Ein leeres Feld entfernt den Link.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setLinkOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" onClick={applyLink}>
              Übernehmen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
